import { jsx, jsxs } from "react/jsx-runtime";
import { Box, Stack, styled } from "@mui/material";
import {
  forwardRef,
  useCallback,
  useEffect,
  useRef
} from "react";
import { isDev } from "../../../../base/isDev.js";
import { useElementSize } from "../../../../hooks/index.js";
import {
  useOnSubmitForm
} from "../../../core/hooks/useOnSubmitForm.js";
import { FormContent } from "./FormContent/FormContent.js";
import { FormValidationIndicator } from "./FormValidationIndicator/FormValidationIndicator.js";
function FieldsContainer(props) {
  const { children, testId = "fields-container-root" } = props;
  return /* @__PURE__ */ jsx(
    Box,
    {
      "data-testid": testId,
      sx: {
        // Spacer that allow the label to not be cut (outlined label will overflow the container)
        paddingTop: "5px"
      },
      children
    }
  );
}
const FormContainer = forwardRef(function FormContainer2(props, ref) {
  const { children, width = "100%", testId = "form-container-root" } = props;
  return /* @__PURE__ */ jsx(Box, { ref, "data-testid": testId, width, flexDirection: "column", display: "flex", flex: 1, children });
});
const StyledForm = styled("form", { name: "Form", slot: "Root" })(() => ({
  width: "100%",
  minHeight: "100%",
  display: "flex",
  position: "relative",
  flex: 1
}));
const noop = () => {
};
function FormLayout(props) {
  const {
    sx,
    header,
    footer,
    testId = "tidal-form-root",
    onSubmit: userOnSubmit,
    onSubmitConfig = {},
    onSubmitError,
    formProps,
    children,
    className = "",
    disableScroll = false,
    fieldsSpacing = 2,
    fieldsPadding = "0 10px",
    hideValidationIndicator = false,
    validationIndicatorProps = {},
    disablePreventDefaultOnEnterPressed
  } = props;
  const { duration, color, size = 100 } = validationIndicatorProps;
  const rTarget = useRef(null);
  const { height, resizing } = useElementSize(rTarget.current);
  useEffect(() => {
    if (isDev()) {
      if (resizing) return;
      const timeout = setTimeout(() => {
        if (height === 0) {
          console.log(
            `[useComputeOutOfViewportHeight] - the height of the scrollbar is 0. Usually, this is because the container doesn't have a height.`
          );
        }
      }, 100);
      return () => clearTimeout(timeout);
    }
  }, [height, resizing]);
  const addHiddenInput = !!userOnSubmit || !!disablePreventDefaultOnEnterPressed;
  const { onSubmit } = useOnSubmitForm(
    userOnSubmit ?? noop,
    onSubmitConfig
  );
  const handleKeyPress = useCallback(
    (event) => {
      const target = event.target;
      const isEnter = event.code === "Enter" || event.key === "Enter" || event.which === 13;
      if (!isEnter) return;
      if (target.type === "textarea") return;
      const onSubmitExists = !!userOnSubmit;
      const preventDefaultBehavior = onSubmitExists ? false : !disablePreventDefaultOnEnterPressed;
      if (preventDefaultBehavior) {
        event.preventDefault();
      }
    },
    [userOnSubmit, disablePreventDefaultOnEnterPressed]
  );
  const fixedMode = props.disableScroll === true ? void 0 : props.fixedFooterMode;
  const scrollDisabledContent = /* @__PURE__ */ jsx(FormContent, { header, footer, disableScroll, children: /* @__PURE__ */ jsx(FieldsContainer, { children: /* @__PURE__ */ jsx(Stack, { spacing: fieldsSpacing, sx: { padding: fieldsPadding }, "data-testid": "fieldsContainer", children }) }) });
  const scrollEnabledContent = /* @__PURE__ */ jsx(FormContent, { header, footer, fixedMode, children: /* @__PURE__ */ jsx(FieldsContainer, { children: /* @__PURE__ */ jsx(Stack, { spacing: fieldsSpacing, sx: { padding: fieldsPadding }, "data-testid": "fieldsContainer", children }) }) });
  const content = disableScroll ? scrollDisabledContent : scrollEnabledContent;
  const showIndicator = !hideValidationIndicator;
  const validationIndicator = showIndicator ? /* @__PURE__ */ jsx(FormValidationIndicator, { size, duration, color }) : null;
  return /* @__PURE__ */ jsx(
    StyledForm,
    {
      "data-testid": testId,
      "data-tidal": "TidalForm",
      method: "_self",
      ...formProps,
      className,
      onKeyDown: handleKeyPress,
      onSubmit: async (event) => {
        if (!disablePreventDefaultOnEnterPressed) event.preventDefault();
        const result = await onSubmit(event);
        if (result.status !== "success") onSubmitError?.(result);
      },
      sx,
      children: /* @__PURE__ */ jsxs(FormContainer, { ref: rTarget, children: [
        validationIndicator,
        content,
        addHiddenInput && /* @__PURE__ */ jsx("input", { type: "submit", hidden: true })
      ] })
    }
  );
}
export {
  FormLayout
};
//# sourceMappingURL=FormLayout.js.map
